/**
 * Thin fetch wrapper around the `@grafx-vdp/api` Hono Worker (`/api/*` routes).
 *
 * - Reads the bearer JWT from the `AuthProvider` context (passed via closure).
 * - Resolves the API base URL from `VITE_ADMIN_API_URL`, falling back to
 *   `http://localhost:8787` in dev and `https://api.grafx-vdp.kadanza.app`
 *   in production.
 * - Maps non-OK responses to typed `ApiError` instances so React Query can
 *   distinguish 401/403/404/409/500 etc.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly body: unknown;

  constructor(status: number, code: string | null, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  isUnauthorized(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }

  isNotFound(): boolean {
    return this.status === 404;
  }

  isConflict(): boolean {
    return this.status === 409;
  }

  isServerError(): boolean {
    return this.status >= 500;
  }
}

const DEFAULT_DEV_URL = 'http://localhost:8787';
const DEFAULT_PROD_URL = 'https://api.grafx-vdp.kadanza.app';

/**
 * Global "the API rejected us with a 401" hook.
 *
 * `apiFetch` calls `notifyUnauthorized()` whenever the API returns 401, and the
 * app registers a handler (via `setUnauthorizedHandler`) that re-runs the auth
 * flow (token refresh, falling back to a full re-login). Kept as a module-level
 * bridge because the single `QueryClient` and `apiFetch` live outside React,
 * while the actual refresh callback comes from the handshake/auth context.
 *
 * Triggers are debounced so a burst of parallel 401s (e.g. the runs + flows +
 * dataset requests all firing at once) only kicks off a single re-auth.
 */
type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;
let lastUnauthorizedAt = 0;

const UNAUTHORIZED_DEBOUNCE_MS = 3_000;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

function notifyUnauthorized(): void {
  const now = Date.now();
  if (now - lastUnauthorizedAt < UNAUTHORIZED_DEBOUNCE_MS) return;
  lastUnauthorizedAt = now;
  unauthorizedHandler?.();
}

export function resolveBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_ADMIN_API_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return import.meta.env.DEV ? DEFAULT_DEV_URL : DEFAULT_PROD_URL;
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  jwt: string | null;
  query?: Record<string, string | number | boolean | undefined | null>;
  json?: unknown;
  headers?: Record<string, string>;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = resolveBaseUrl();
  const url = new URL(path.startsWith('/') ? path : `/${path}`, base);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions): Promise<T> {
  const { jwt, query, json, headers, ...rest } = opts;

  const url = buildUrl(path, query);
  const reqHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(headers ?? {}),
  };
  if (jwt) reqHeaders.Authorization = `Bearer ${jwt}`;
  if (json !== undefined) reqHeaders['Content-Type'] = 'application/json';

  const response = await fetch(url, {
    ...rest,
    headers: reqHeaders,
    body: json !== undefined ? JSON.stringify(json) : undefined,
  });

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const parsed: unknown = isJson
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const body = parsed as { error?: string; message?: string } | string | null;
    const code = body && typeof body === 'object' && 'error' in body ? body.error ?? null : null;
    const message =
      (body && typeof body === 'object' && 'message' in body && body.message) ||
      response.statusText ||
      `Request failed with status ${response.status}`;
    // A 401 means our JWT is missing/expired/invalid — re-run the auth flow.
    if (response.status === 401) notifyUnauthorized();
    throw new ApiError(response.status, code, message, parsed);
  }

  return parsed as T;
}

/**
 * Fetch a binary endpoint (e.g. a run artifact download) as a `Blob`, with the
 * same auth + base-URL + 401 handling as {@link apiFetch}. Returns the blob and
 * the server-suggested filename (parsed from `Content-Disposition`, if any).
 */
export async function apiFetchBlob(
  path: string,
  opts: { jwt: string | null; query?: RequestOptions['query'] },
): Promise<{ blob: Blob; filename: string | null }> {
  const url = buildUrl(path, opts.query);
  const reqHeaders: Record<string, string> = {};
  if (opts.jwt) reqHeaders.Authorization = `Bearer ${opts.jwt}`;

  const response = await fetch(url, { method: 'GET', headers: reqHeaders });
  if (!response.ok) {
    if (response.status === 401) notifyUnauthorized();
    const text = await response.text().catch(() => '');
    throw new ApiError(response.status, null, text || response.statusText, text);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] ? decodeURIComponent(match[1]) : null;
  return { blob, filename };
}
